import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import adminApi from '../../lib/adminApi';
import { useViewMode } from '../../contexts/ViewModeContext';
import { 
  LayoutDashboard, CheckSquare, Users, Building, Briefcase, 
  HelpCircle, FileText, Settings, LogOut, User,
  TrendingUp, UserCheck, Building2, CircleAlert, Calendar, BookOpen, UserCog,
  Clock, AlertTriangle, RefreshCw, CalendarCheck, CalendarClock, Flame, Zap, ListTodo, FolderKanban,
  Calculator, Loader2, Megaphone, Award, Activity, Trophy,
  ChevronDown, ChevronRight, Wallet, Receipt, Headphones, Shield, ShieldCheck, Database, BarChart3, ClipboardList, CalendarDays,
  BellRing,
} from 'lucide-react';
import AdminTasksPage from '../../components/AdminTasksPage';
import AdminDataSnapshotPage from '../../components/AdminDataSnapshotPage';
import DataIOPage from '../../components/DataIOPage';
import AdminReportsPage from '../../components/AdminReportsPage';
import EtimeofficeSettingsPage from '../../components/EtimeofficeSettingsPage';
import AdminAttendancePage from './AdminAttendancePage';
import AdminReportsHub from '../../components/reports/AdminReportsHub';
import EtimeofficeQueuePage from '../../components/EtimeofficeQueuePage';
import AdminClientsPage from '../../components/AdminClientsPage';
import AdminOrganisationPage from '../../components/AdminOrganisationPage';
import AdminAuditLogsPage from '../../components/AdminAuditLogsPage';
import AdminSettingsPage from '../../components/AdminSettingsPage';
import AdminHelpDeskPage from '../../components/AdminHelpDeskPage';
import CalendarsPage from '../../components/CalendarsPage';
import NotificationBell from '../../components/NotificationBell';
import { ViewModeToggleSimple } from '../../components/ViewModeToggle';
import MobileHeader from '../../components/MobileHeader';
import MobileBottomNav from '../../components/MobileBottomNav';
import MobileFAB from '../../components/MobileFAB';
import MobileSidebar from '../../components/MobileSidebar';
import EmailQuotaBanner from '../../components/EmailQuotaBanner';
import AdminUserManagement from '../../components/AdminUserManagement';
import PushNotificationsPage from '../../components/push/PushNotificationsPage';
import AdminRRConsole from '../../components/rr/AdminRRConsole';
import SalaryRegisterPage from '../../components/salary/SalaryRegisterPage';
import { formatDateToLocal } from '../../lib/dateUtils';
import { DashboardSkeleton, MetricsSkeleton } from '../../components/DashboardSkeleton';
import { AdminMetricsDashboard } from '../../components/MetricsDashboard';
import AdminActionCenter from '../../components/AdminActionCenter';
import NoticeBoard from '../../components/NoticeBoard';
import SectionErrorBoundary from '../../components/SectionErrorBoundary';
import AdminNoticeManagement from '../../components/AdminNoticeManagement';

// Import HumanResourcesPage component
import HumanResourcesPage from '../../components/HumanResourcesPage';

// Import AccountingPage component
import AccountingPage from '../../components/AccountingPage';

// Analytics & Performance pages removed (Feb 2026) — admin data-preview pages
// were deleted as they were not in active use. Backend route modules remain
// dormant for the AI Admin Agent's natural-language analytics queries.

// Import ChatWidget for internal messaging
// (Removed — replaced by AI Agent widget for admin)
import VersionChip from '../../components/VersionChip';

// AI Agent (Phase 1) — strictly behind feature flag. Removable by deleting
// this lazy import + the <AgentWidget /> mount below.
const AgentWidget = process.env.REACT_APP_ENABLE_AI_AGENT === 'true'
  ? React.lazy(() => import('../../components/agent/AgentWidget'))
  : null;

// Import Badge Management
import AdminBadgeManagement from '../../components/AdminBadgeManagement';

// Import Health Alerts Hub for lifecycle management
import HealthAlertsHub from '../../components/HealthAlertsHub';

// Import Data Health Page for accounting audit
import DataHealthPage from '../../components/DataHealthPage';

// AI Agent admin usage page — gated like AgentWidget
const AgentUsagePage = process.env.REACT_APP_ENABLE_AI_AGENT === 'true'
  ? React.lazy(() => import('../../components/agent/AgentUsagePage'))
  : null;

// Sidebar Group Component — Charcoal + Pink accents
const SidebarGroup = ({ group, activeMenu, onMenuChange, expandedGroups, toggleGroup }) => {
  const isExpanded = expandedGroups.includes(group.key);
  
  return (
    <div className="mb-1">
      <button onClick={() => toggleGroup(group.key)}
        className="w-full flex items-center justify-between px-3 py-2 rounded-2xl transition-colors hover:bg-white/[0.06]">
        <div className="flex items-center gap-2.5">
          <group.icon className="w-3.5 h-3.5 text-white/30" />
          <span className="text-[11px] font-semibold tracking-wider text-white/30">{group.label}</span>
        </div>
        <ChevronRight className={`w-3.5 h-3.5 text-white/20 transition-transform duration-300 ${isExpanded ? 'rotate-90' : ''}`} />
      </button>
      
      {isExpanded && (
        <div className="mt-0.5 space-y-0.5 pl-1">
          {group.items.map((item) => {
            const Icon = item.icon;
            const isActive = activeMenu === item.key;
            if (item.onClick) {
              return (
                <button key={item.key} onClick={item.onClick}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-2xl text-white/60 hover:bg-white/[0.06] hover:text-white transition-all duration-200"
                  data-testid={`desktop-menu-${item.key}`}>
                  <Icon className="w-4 h-4" /><span className="text-[13px]">{item.label}</span>
                </button>
              );
            }
            return (
              <button key={item.key} onClick={() => onMenuChange(item.key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-2xl transition-all duration-200 ${
                  isActive 
                    ? 'bg-[#FF6B8A]/15 text-[#FF6B8A] font-semibold' 
                    : 'text-white/60 hover:bg-white/[0.06] hover:text-white'
                }`}
                data-testid={`desktop-menu-${item.key}`}>
                <Icon className="w-4 h-4" /><span className="text-[13px]">{item.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default function NewAdminDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobileView } = useViewMode();
  
  // Get initial page from URL or default to 'dashboard'
  const initialPage = searchParams.get('page') || 'dashboard';
  // ─── Legacy URL aliases (Feb 2026 consolidation) ────────────────────────
  // Standalone Attendance / Approval Queue pages were folded into Reports.
  const LEGACY_ALIASES = {
    'attendance': { menu: 'reports', extra: { reports_category: 'attendance', attendance_subtab: 'snapshot' } },
    'etimeoffice-queue': { menu: 'reports', extra: { reports_category: 'attendance', attendance_subtab: 'queue' } },
  };
  const aliased = LEGACY_ALIASES[initialPage];
  if (aliased && typeof window !== 'undefined') {
    const usp = new URLSearchParams(window.location.search);
    usp.set('page', aliased.menu);
    Object.entries(aliased.extra).forEach(([k, v]) => usp.set(k, v));
    window.history.replaceState(null, '', `${window.location.pathname}?${usp.toString()}`);
  }
  const [activeMenu, setActiveMenu] = useState(aliased ? aliased.menu : initialPage);
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [adminData, setAdminData] = useState(null);
  const [taskFilter, setTaskFilter] = useState(null);
  const [taskStats, setTaskStats] = useState({
    totalTasks: 0,
    newTasks: 0,
    overdueTasks: 0,
    revisionTasks: 0,
    dueToday: 0,
    dueTomorrow: 0,
    dueThisWeek: 0,
    highPriority: 0,
    criticalTasks: 0,
    inProgress: 0
  });
  const [dashboardData, setDashboardData] = useState({
    activeTasks: 0,
    activeTasksChange: 0,
    pendingApplications: 0,
    pendingApplicationsChange: 0,
    activeClients: 0,
    activeClientsChange: 0,
    openTickets: 0,
    openTicketsChange: 0,
    recentActivity: [],
    pendingLeaveRequests: [],
    tasksOverview: { pending: 0, inProgress: 0, completed: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [metricsLoaded, setMetricsLoaded] = useState(false); // Tier 1: instant metrics

  // Sync activeMenu with URL search params
  useEffect(() => {
    const page = searchParams.get('page');
    if (page && page !== activeMenu) {
      setActiveMenu(page);
    }
  }, [searchParams]);

  // Update URL when activeMenu changes (internal helper)
  const updateUrlWithMenu = (menu) => {
    if (menu === 'dashboard') {
      searchParams.delete('page');
    } else {
      searchParams.set('page', menu);
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Fetch admin profile to get role
  useEffect(() => {
    fetchAdminProfile();
  }, []);

  const fetchAdminProfile = async () => {
    try {
      // Try V1 first, fallback to old endpoint
      let response;
      try {
        response = await adminApi.get('/api/v1/auth/me');
      } catch {
        response = await adminApi.get('/api/admin/auth/me');
      }
      setAdminData(response.data);
      
      // Role-based routing: restrict access to assigned module only
      const role = response.data.role;
      if (role === 'hr') {
        // HR now has full parity with admin on dashboard/notices/recognitions —
        // land them on the dashboard (with the notice bar & team absences).
        setActiveMenu('dashboard');
        updateUrlWithMenu('dashboard');
      } else if (role === 'accountant') {
        setActiveMenu('accounting');
        updateUrlWithMenu('accounting');
      }
    } catch (error) {
      console.error('Error fetching admin profile:', error);
    }
  };

  useEffect(() => {
    // Only fetch dashboard data if user has full access
    // HR now has dashboard parity with admin (announcements, notices, recognitions, team absences)
    const restrictedRoles = ['accountant'];
    if (!restrictedRoles.includes(adminData?.role)) {
      // PERFORMANCE: Use tiered progressive loading
      fetchMetricsFirst(); // Tier 1: Instant metrics (~50ms)
    } else {
      setLoading(false);
      setMetricsLoaded(true);
    }
  }, [adminData]);

  // TIER 1: Fetch metrics first (instant - <100ms)
  const fetchMetricsFirst = async () => {
    try {
      // Try new optimized metrics endpoint first
      const metricsRes = await adminApi.get('/api/v2/admin/dashboard/metrics');
      const metrics = metricsRes.data;
      
      // Immediately update task stats from metrics
      setTaskStats({
        totalTasks: metrics.tasks?.total || 0,
        newTasks: metrics.tasks?.new || 0,
        overdueTasks: metrics.tasks?.overdue || 0,
        revisionTasks: metrics.tasks?.revision || 0,
        dueToday: metrics.tasks?.dueToday || 0,
        dueTomorrow: metrics.tasks?.dueTomorrow || 0,
        dueThisWeek: metrics.tasks?.dueThisWeek || 0,
        highPriority: metrics.tasks?.highPriority || 0,
        criticalTasks: metrics.tasks?.critical || 0,
        inProgress: metrics.tasks?.inProgress || 0
      });
      
      // Update dashboard counts
      setDashboardData(prev => ({
        ...prev,
        activeClients: metrics.clients?.active || 0,
        openTickets: metrics.tickets?.open || 0
      }));
      
      setMetricsLoaded(true);
      
      // Now fetch full dashboard data in background (Tier 2)
      fetchDashboardData();
      
    } catch (error) {
      console.warn('Metrics endpoint failed, falling back to full load:', error.message);
      // Fallback to original loading method
      fetchDashboardData();
      fetchTaskStats();
    }
  };

  const fetchTaskStats = async () => {
    try {
      // Use V1 endpoint with pagination
      let allTasks = [];
      try {
        const response = await adminApi.get('/api/v1/tasks', { params: { page_size: 500 } });
        allTasks = response.data.items || response.data || [];
      } catch {
        // Fallback to old endpoint
        const response = await adminApi.get('/api/admin/tasks');
        allTasks = response.data || [];
      }
      
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const weekEnd = new Date(today);
      weekEnd.setDate(weekEnd.getDate() + 7);
      
      const formatDate = (d) => formatDateToLocal(d);
      
      setTaskStats({
        totalTasks: allTasks.filter(t => t.status !== 'Completed').length,
        newTasks: allTasks.filter(t => t.status === 'New').length,
        overdueTasks: allTasks.filter(t => {
          if (!t.due_date || t.status === 'Completed') return false;
          const taskDate = new Date(t.due_date + 'T00:00:00');
          return taskDate < today;
        }).length,
        revisionTasks: allTasks.filter(t => t.status === 'Revision').length,
        dueToday: allTasks.filter(t => t.due_date === formatDate(today) && t.status !== 'Completed').length,
        dueTomorrow: allTasks.filter(t => t.due_date === formatDate(tomorrow) && t.status !== 'Completed').length,
        dueThisWeek: allTasks.filter(t => {
          if (!t.due_date || t.status === 'Completed') return false;
          const dueDate = new Date(t.due_date + 'T00:00:00');
          return dueDate >= today && dueDate <= weekEnd;
        }).length,
        highPriority: allTasks.filter(t => t.priority === 'High' && t.status !== 'Completed').length,
        criticalTasks: allTasks.filter(t => t.priority === 'Critical' && t.status !== 'Completed').length,
        inProgress: allTasks.filter(t => t.status === 'In Progress').length
      });
    } catch (error) {
      console.error('Error fetching task stats:', error);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Try V1 first, fallback to old endpoint
      let response;
      try {
        response = await adminApi.get('/api/v1/dashboard');
      } catch {
        response = await adminApi.get('/api/admin/dashboard');
      }
      const data = response.data;
      
      setDashboardData({
        activeTasks: data.activeTasks || 0,
        activeTasksChange: data.activeTasksChange || 0,
        pendingApplications: data.pendingApplications || 0,
        pendingApplicationsChange: data.pendingApplicationsChange || 0,
        activeClients: data.activeClients || 0,
        activeClientsChange: data.activeClientsChange || 0,
        openTickets: data.openTickets || 0,
        openTicketsChange: data.openTicketsChange || 0,
        recentActivity: data.recentActivity || [],
        pendingLeaveRequests: data.pendingLeaveRequests || [],
        tasksOverview: data.tasksOverview || { pending: 0, inProgress: 0, completed: 0 }
      });
      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  // Check if current user is super admin
  const isSuperAdmin = () => {
    return adminData?.email === 'admin@halfeatendonut.in' || adminData?.role === 'superadmin';
  };

  // State for expanded sidebar groups
  const [expandedGroups, setExpandedGroups] = useState(['operations', 'people', 'clients', 'finance']);

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => 
      prev.includes(groupKey) 
        ? prev.filter(g => g !== groupKey)
        : [...prev, groupKey]
    );
  };

  // Grouped menu structure - Option A
  const menuGroups = [
    {
      key: 'operations',
      label: 'Operations',
      icon: LayoutDashboard,
      items: [
        { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { key: 'tasks', label: 'Tasks', icon: CheckSquare },
        { key: 'calendars', label: 'Calendars', icon: Calendar },
        { key: 'reports', label: 'Reports', icon: BarChart3 },
        { key: 'etimeoffice', label: 'Attendance Machine', icon: Clock },
      ]
    },
    {
      key: 'people',
      label: 'People',
      icon: Users,
      items: [
        { key: 'organisation', label: 'Employees', icon: Users },
        { key: 'hr', label: 'HR & Leaves', icon: UserCheck },
        { key: 'badges', label: 'Recognition', icon: Award },
        { key: 'rewards', label: 'R&R Nominations', icon: Trophy },
        { key: 'salary-register', label: 'Salary Register', icon: Wallet },
      ]
    },
    {
      key: 'clients',
      label: 'Clients',
      icon: Briefcase,
      items: [
        { key: 'clients', label: 'Client Management', icon: Briefcase },
      ]
    },
    {
      key: 'finance',
      label: 'Finance',
      icon: Wallet,
      items: [
        { key: 'accounting', label: 'Accounting', icon: Calculator },
        { key: 'data-health', label: 'Data Health', icon: ShieldCheck },
      ]
    },
    {
      key: 'support',
      label: 'Support',
      icon: Headphones,
      items: [
        { key: 'helpdesk', label: 'Help Desk', icon: HelpCircle },
        { key: 'notices', label: 'Notices', icon: Megaphone },
      ]
    },
    {
      key: 'admin',
      label: 'Administration',
      icon: Shield,
      items: [
        { key: 'logs', label: 'Audit Logs', icon: FileText },
        ...(isSuperAdmin() ? [{ key: 'users', label: 'User Management', icon: UserCog }] : []),
        ...(isSuperAdmin() ? [{ key: 'push', label: 'Push Notifications', icon: BellRing }] : []),
        ...(isSuperAdmin() ? [{ key: 'data-snapshot', label: 'Data Snapshot', icon: Database }] : []),
        ...(isSuperAdmin() ? [{ key: 'data-io', label: 'Data Export / Import', icon: Database }] : []),
        ...(AgentUsagePage ? [{ key: 'agent-usage', label: 'AI Agent Usage', icon: Activity }] : []),
        { key: 'settings', label: 'Settings', icon: Settings },
        { key: 'whitepaper', label: 'Whitepaper', icon: BookOpen, onClick: () => handleDownloadWhitepaper() },
      ]
    },
  ];

  // Flat menu items for backward compatibility (used by mobile nav, etc.)
  const allMenuItems = menuGroups.flatMap(group => group.items);

  // Role-specific menu items
  // HR gets end-to-end people-ops access: employees, leaves, hiring, recognition,
  // notices, help desk, audit trail. Excluded: tasks, calendars,
  // clients, accounting, salary register (payroll is finance-scoped),
  // settings, user management. — Updated Feb-2026 audit.
  const hrMenuItems = [
    { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { key: 'organisation', label: 'Employees', icon: Users },
    { key: 'hr', label: 'HR & Hiring', icon: UserCheck },
    { key: 'badges', label: 'Recognition', icon: Award },
    { key: 'rewards', label: 'R&R Nominations', icon: Trophy },
    { key: 'notices', label: 'Notices', icon: Megaphone },
    { key: 'helpdesk', label: 'Help Desk', icon: HelpCircle },
    { key: 'logs', label: 'Audit Logs', icon: FileText },
  ];

  const accountantMenuItems = [
    { key: 'accounting', label: 'Accounting', icon: Calculator },
    { key: 'clients', label: 'Clients', icon: Users },
    { key: 'data-health', label: 'Data Health', icon: ShieldCheck },
  ];

  // Get menu items based on role — enforces sidebar restriction.
  // While the profile is still loading (adminData null) we return an EMPTY
  // list so restricted roles never see forbidden menu items flash on mount.
  const getMenuItems = () => {
    if (!adminData) return [];
    const role = adminData?.role;
    if (role === 'hr') return hrMenuItems;
    if (role === 'accountant') return accountantMenuItems;
    return allMenuItems;
  };

  const menuItems = getMenuItems();

  // Get the set of allowed menu keys for this role
  const allowedMenuKeys = new Set(menuItems.map(m => m.key));

  // Filter menu groups to only show groups that have at least one allowed item
  const filteredMenuGroups = menuGroups
    .map(group => ({
      ...group,
      items: group.items.filter(item => allowedMenuKeys.has(item.key))
    }))
    .filter(group => group.items.length > 0);

  const handleDownloadWhitepaper = async () => {
    try {
      toast.info('Generating PDF whitepaper...');
      const response = await adminApi.get('/api/admin/whitepaper/download', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Girgit_Product_Whitepaper.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Whitepaper downloaded successfully');
    } catch (error) {
      console.error('Error downloading whitepaper:', error);
      toast.error('Failed to download whitepaper');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  // Handle menu change with role restriction
  const handleMenuChange = (key) => {
    // Enforce role-based access — only allow access to permitted menus
    if (!allowedMenuKeys.has(key)) {
      const roleName = adminData?.role === 'hr' ? 'Human Resources' : 
                       adminData?.role === 'accountant' ? 'Accounting' : 'your assigned modules';
      toast.error(`You only have access to ${roleName}`);
      return;
    }
    setActiveMenu(key);
    updateUrlWithMenu(key);
    setTaskFilter(null); // Clear filter when changing menu
  };

  // Navigate to tasks with filter
  const handleStatClick = (filterType) => {
    setTaskFilter(filterType);
    setActiveMenu('tasks');
    updateUrlWithMenu('tasks');
  };

  // Dashboard Stats Configuration (10 stats - 5 per row desktop, 2 per row mobile)
  const dashboardStats = [
    { key: 'totalTasks', label: 'Total Tasks', value: taskStats.totalTasks, icon: ListTodo, color: 'blue', filter: 'all' },
    { key: 'newTasks', label: 'New Tasks', value: taskStats.newTasks, icon: Clock, color: 'cyan', filter: 'new' },
    { key: 'inProgress', label: 'In Progress', value: taskStats.inProgress, icon: FolderKanban, color: 'purple', filter: 'inprogress' },
    { key: 'overdueTasks', label: 'Overdue Tasks', value: taskStats.overdueTasks, icon: AlertTriangle, color: 'red', filter: 'overdue' },
    { key: 'revisionTasks', label: 'Revision Tasks', value: taskStats.revisionTasks, icon: RefreshCw, color: 'orange', filter: 'revision' },
    { key: 'dueToday', label: 'Due Today', value: taskStats.dueToday, icon: CalendarCheck, color: 'emerald', filter: 'today' },
    { key: 'dueTomorrow', label: 'Due Tomorrow', value: taskStats.dueTomorrow, icon: CalendarClock, color: 'teal', filter: 'tomorrow' },
    { key: 'dueThisWeek', label: 'Due This Week', value: taskStats.dueThisWeek, icon: Calendar, color: 'indigo', filter: 'week' },
    { key: 'highPriority', label: 'High Priority', value: taskStats.highPriority, icon: Flame, color: 'rose', filter: 'high' },
    { key: 'criticalTasks', label: 'Critical Tasks', value: taskStats.criticalTasks, icon: Zap, color: 'fuchsia', filter: 'critical' }
  ];

  const colorClasses = {
    blue: { bg: 'bg-blue-50', border: 'border-blue-100', iconBg: 'bg-blue-100', iconText: 'text-blue-600', text: 'text-blue-600', gradient: 'from-[#3366FF] to-[#3366FF]' },
    cyan: { bg: 'bg-cyan-50', border: 'border-cyan-100', iconBg: 'bg-cyan-100', iconText: 'text-cyan-600', text: 'text-cyan-600', gradient: 'from-cyan-500 to-cyan-600' },
    purple: { bg: 'bg-purple-50', border: 'border-purple-100', iconBg: 'bg-purple-100', iconText: 'text-purple-600', text: 'text-purple-600', gradient: 'from-purple-500 to-purple-600' },
    red: { bg: 'bg-red-50', border: 'border-red-100', iconBg: 'bg-red-100', iconText: 'text-red-600', text: 'text-red-600', gradient: 'from-red-500 to-red-600' },
    orange: { bg: 'bg-orange-50', border: 'border-orange-100', iconBg: 'bg-orange-100', iconText: 'text-orange-600', text: 'text-orange-600', gradient: 'from-orange-500 to-orange-600' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-100', iconBg: 'bg-emerald-100', iconText: 'text-emerald-600', text: 'text-emerald-600', gradient: 'from-emerald-500 to-emerald-600' },
    teal: { bg: 'bg-teal-50', border: 'border-teal-100', iconBg: 'bg-teal-100', iconText: 'text-teal-600', text: 'text-teal-600', gradient: 'from-teal-500 to-teal-600' },
    indigo: { bg: 'bg-indigo-50', border: 'border-indigo-100', iconBg: 'bg-indigo-100', iconText: 'text-indigo-600', text: 'text-indigo-600', gradient: 'from-indigo-500 to-indigo-600' },
    rose: { bg: 'bg-rose-50', border: 'border-rose-100', iconBg: 'bg-rose-100', iconText: 'text-rose-600', text: 'text-rose-600', gradient: 'from-rose-500 to-rose-600' },
    fuchsia: { bg: 'bg-fuchsia-50', border: 'border-fuchsia-100', iconBg: 'bg-fuchsia-100', iconText: 'text-fuchsia-600', text: 'text-fuchsia-600', gradient: 'from-fuchsia-500 to-fuchsia-600' }
  };

  // Get menu title for mobile header
  const getMenuTitle = () => {
    const item = menuItems.find(m => m.key === activeMenu);
    return item?.label || (adminData?.role === 'hr' ? 'Human Resources' : 
                           adminData?.role === 'accountant' ? 'Accounting' : 'Dashboard');
  };

  // Get display name for user
  const getUserName = () => {
    return adminData?.full_name || 'Admin User';
  };

  // Get role display name
  const getUserRole = () => {
    if (adminData?.role === 'hr') return 'HR Administrator';
    if (adminData?.role === 'accountant') return 'Accountant';
    return 'Administrator';
  };

  // ============================================
  // ROLE HYDRATION GATE — never render tabs / dashboard content before we
  // know who the user is. Prevents forbidden sections from flashing on the
  // screen for accountants / HR during initial profile fetch or reloads.
  // ============================================
  if (!adminData) {
    return (
      <div className="min-h-screen bg-[#F7F6F9] dark:bg-[#111111] flex items-center justify-center" data-testid="admin-dashboard-role-gate">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-fuchsia-300 border-t-fuchsia-600 rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-sm text-neutral-500">Loading workspace…</p>
        </div>
      </div>
    );
  }

  // ============================================
  // MOBILE VIEW
  // ============================================
  if (isMobileView) {
    return (
      <div className="min-h-screen bg-[#F7F6F9] dark:bg-[#111111]" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 5rem)' }} data-testid="admin-dashboard-mobile">
        {/* Super-admin only email quota banner */}
        <EmailQuotaBanner />

        {/* Mobile Header */}
        <MobileHeader
          onMenuOpen={() => setIsSidebarOpen(true)}
          api={adminApi}
          portalType="admin"
          title={getMenuTitle()}
        />

        {/* Mobile Sidebar */}
        <MobileSidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          menuItems={menuItems}
          activeMenu={activeMenu}
          setActiveMenu={handleMenuChange}
          onLogout={handleLogout}
          userName={getUserName()}
          userRole={getUserRole()}
          portalType="admin"
        />

        {/* Main Content - Mobile */}
        <main className="px-3 py-3">
          {activeMenu === 'dashboard' && !['accountant'].includes(adminData?.role) && (
            <div data-testid="admin-dashboard-content-mobile">
              <div className="mb-4">
                <h1 className="text-lg font-bold text-[#1a1a2e]">Dashboard</h1>
                <p className="text-xs text-neutral-500">Welcome back! Here&apos;s what&apos;s happening today.</p>
              </div>

              <div className="mb-4">
                <SectionErrorBoundary label="Metrics">
                  <AdminMetricsDashboard 
                    api={adminApi} 
                    onTaskFilter={(filter) => { setTaskFilter(filter); handleMenuChange('tasks'); }}
                  />
                </SectionErrorBoundary>
              </div>

              <div className="mb-4">
                <AdminActionCenter api={adminApi} onNavigate={handleMenuChange} />
              </div>

              {/* Quick Actions — Glassy Mobile */}
              <div className="glass-card p-4 mb-4">
                <h2 className="text-sm font-semibold text-[#1a1a2e] mb-3">Quick Actions</h2>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => handleMenuChange('tasks')}
                    className="p-4 bg-white/60 border border-white/40 rounded-2xl hover:bg-white/80 transition-all text-center">
                    <CheckSquare className="w-6 h-6 text-brutal-pink mx-auto mb-2" />
                    <p className="text-sm font-medium text-[#1a1a2e]">View Tasks</p>
                  </button>
                  <button onClick={() => handleMenuChange('clients')}
                    className="p-4 bg-white/60 border border-white/40 rounded-2xl hover:bg-white/80 transition-all text-center">
                    <Building2 className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-[#1a1a2e]">View Clients</p>
                  </button>
                  <button onClick={() => handleMenuChange('organisation')}
                    className="p-4 bg-white/60 border border-white/40 rounded-2xl hover:bg-white/80 transition-all text-center">
                    <Users className="w-6 h-6 text-purple-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-[#1a1a2e]">Organisation</p>
                  </button>
                  <button onClick={() => handleMenuChange('helpdesk')}
                    className="p-4 bg-white/60 border border-white/40 rounded-2xl hover:bg-white/80 transition-all text-center">
                    <HelpCircle className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                    <p className="text-sm font-medium text-[#1a1a2e]">Help Desk</p>
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <SectionErrorBoundary label="Notice Board">
                  <NoticeBoard role="admin" />
                </SectionErrorBoundary>
              </div>
            </div>
          )}

          {/* Other Pages */}
          {activeMenu === 'tasks' && <AdminTasksPage initialFilter={taskFilter} onClearFilter={() => setTaskFilter(null)} />}
          {activeMenu === 'calendars' && <SectionErrorBoundary label="Calendars"><CalendarsPage api={adminApi} userType="admin" /></SectionErrorBoundary>}
          {activeMenu === 'hr' && <HumanResourcesPage />}
          {activeMenu === 'clients' && <AdminClientsPage />}
          {activeMenu === 'organisation' && <AdminOrganisationPage />}
          {activeMenu === 'logs' && <AdminAuditLogsPage />}
          {activeMenu === 'settings' && <AdminSettingsPage />}
          {activeMenu === 'helpdesk' && <AdminHelpDeskPage />}
          {activeMenu === 'users' && <AdminUserManagement />}
          {activeMenu === 'push' && isSuperAdmin() && <PushNotificationsPage />}
          {activeMenu === 'accounting' && <AccountingPage />}
          {activeMenu === 'data-health' && <DataHealthPage />}
          {activeMenu === 'badges' && <AdminBadgeManagement />}
          {activeMenu === 'notices' && (
            <div>
              <div className="mb-4">
                <h1 className="text-lg font-bold text-[#1a1a2e]">Notice Board Management</h1>
                <p className="text-xs text-neutral-500">Create recognitions and company announcements</p>
              </div>
              <AdminNoticeManagement api={adminApi} />
            </div>
          )}
          {activeMenu === 'rewards' && (
            <SectionErrorBoundary label="R&R Nominations"><AdminRRConsole api={adminApi} /></SectionErrorBoundary>
          )}
          {activeMenu === 'salary-register' && (
            <SectionErrorBoundary label="Salary Register"><SalaryRegisterPage api={adminApi} /></SectionErrorBoundary>
          )}
          {activeMenu === 'reports' && <AdminReportsHub />}
          {activeMenu === 'etimeoffice' && <EtimeofficeSettingsPage />}
          {activeMenu === 'data-snapshot' && <AdminDataSnapshotPage />}
          {activeMenu === 'data-io' && <DataIOPage />}
        </main>

        {/* Mobile Bottom Navigation */}
        <MobileBottomNav
          activeMenu={activeMenu}
          setActiveMenu={handleMenuChange}
          onMenuOpen={() => setIsSidebarOpen(true)}
          portalType="admin"
          role={adminData?.role}
        />

        {/* Contextual Floating Action Button — routes to the module's key
            create action based on activeMenu (new PO, add client, etc.). */}
        <MobileFAB
          activeMenu={activeMenu}
          portalType="admin"
          onAction={(action) => {
            const menuMap = {
              'new-task': 'tasks',
              'new-po': 'accounting',
              'new-client': 'clients',
              'new-employee': 'organisation',
              'new-ticket': 'helpdesk',
              'new-notice': 'notices',
            };
            const target = menuMap[action];
            if (target && target !== activeMenu) handleMenuChange(target);
            // Announce the intent globally so pages listening (e.g. AccountingPage)
            // can auto-open their create modals.
            window.dispatchEvent(new CustomEvent('girgit:mobile-fab', { detail: { action } }));
          }}
        />

        {/* AI Agent (admin-only, gated by REACT_APP_ENABLE_AI_AGENT) — replaces the old ChatWidget */}
        {AgentWidget && (
          <React.Suspense fallback={null}>
            <AgentWidget />
          </React.Suspense>
        )}
      </div>
    );
  }

  // ============================================
  // DESKTOP VIEW (Original)
  // ============================================
  return (
    <div className="min-h-screen bg-[#F7F6F9] dark:bg-[#111111] flex" data-testid="admin-dashboard-desktop">
      {/* Sidebar — Charcoal black with pink accents */}
      <aside className="w-[260px] bg-[#111111] flex flex-col fixed inset-y-0 left-0 z-20 rounded-r-[28px] shadow-2xl">
        {/* Brand */}
        <div className="p-5 pt-7">
          <div className="flex items-center gap-2.5">
            <img src="/hed-icon.png" alt="HED" className="w-7 h-7 rounded-lg" />
            <div className="w-px h-5 bg-white/10" />
            <img src="/girgit-logo.png" alt="Girgit" className="w-7 h-7 rounded-lg" />
            <span className="text-white font-bold text-base tracking-tight">Girgit</span>
          </div>
        </div>

        {/* User Info */}
        <div className="px-4 py-3">
          <div className="flex items-center gap-3 bg-white/[0.06] rounded-2xl p-3 border border-white/[0.05]">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#FF6B8A] to-[#E8366D] flex items-center justify-center shadow-lg shadow-pink-500/20">
              <User className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm text-white truncate">{getUserName()}</p>
              <p className="text-[11px] text-white/40">{getUserRole()}</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-1 overflow-y-auto scrollbar-thin">
          {['hr', 'accountant'].includes(adminData?.role) ? (
            menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeMenu === item.key;
              return (
                <button key={item.key} onClick={() => handleMenuChange(item.key)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-2xl mb-0.5 transition-all duration-200 ${
                    isActive 
                      ? 'bg-[#FF6B8A]/15 text-[#FF6B8A] font-semibold' 
                      : 'text-white/60 hover:bg-white/[0.06] hover:text-white'
                  }`}
                  data-testid={`desktop-menu-${item.key}`}>
                  <Icon className="w-4 h-4" /><span className="text-[13px]">{item.label}</span>
                </button>
              );
            })
          ) : (
            menuGroups.map((group) => (
              <SidebarGroup key={group.key} group={group} activeMenu={activeMenu}
                onMenuChange={handleMenuChange} expandedGroups={expandedGroups} toggleGroup={toggleGroup} />
            ))
          )}
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-white/[0.06]">
          <button onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 text-white/40 hover:bg-white/[0.06] hover:text-[#FF6B8A] rounded-2xl transition-all duration-200"
            data-testid="desktop-logout-btn">
            <LogOut className="w-4 h-4" /><span className="text-[13px]">Logout</span>
          </button>
        </div>

        {/* Deploy health / version chip — surfaces frontend bundle hash + backend DB ping */}
        <VersionChip />
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-[260px] overflow-y-auto min-h-screen">
        <EmailQuotaBanner />
        {/* Header — Glassy */}
        <header className="sticky top-0 z-10 bg-white/60 dark:bg-[#111111]/80 backdrop-blur-2xl border-b border-white/40 dark:border-white/[0.06] px-8 py-4 flex items-center justify-end gap-3">
          <ViewModeToggleSimple />
          <NotificationBell api={adminApi} portalType="admin" accentColor="pink" />
        </header>

        {/* Dashboard Content - Hidden for restricted roles */}
        {activeMenu === 'dashboard' && !['accountant'].includes(adminData?.role) && (
          <div className="p-8" data-testid="admin-dashboard-content-desktop">
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-[#1a1a2e] dark:text-white tracking-tight">Dashboard</h1>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">Welcome back! Here&apos;s what&apos;s happening.</p>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
              <div className="xl:col-span-3 space-y-6">
                <SectionErrorBoundary label="Metrics">
                  <AdminMetricsDashboard api={adminApi} onTaskFilter={(filter) => { setTaskFilter(filter); handleMenuChange('tasks'); }} />
                </SectionErrorBoundary>
                <HealthAlertsHub onNavigateToOffboarding={(employeeId) => { handleMenuChange('organisation'); toast.info('Navigate to employee offboarding'); }} onNavigateToTasks={(taskId) => { handleMenuChange('tasks'); }} onNavigateToClients={(clientId) => { handleMenuChange('clients'); }} />
                <AdminActionCenter api={adminApi} onNavigate={handleMenuChange} />

                {/* Quick Actions — Glassy Premium */}
                <div className="glass-card p-6">
                  <h2 className="text-sm font-semibold text-[#1a1a2e] mb-5">Quick Actions</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <button onClick={() => handleMenuChange('tasks')} className="p-5 bg-white/60 backdrop-blur-sm border border-white/40 rounded-2xl hover:bg-white/80 hover:shadow-soft hover:-translate-y-0.5 transition-all duration-200 text-center group" data-testid="quick-action-tasks">
                      <CheckSquare className="w-6 h-6 text-brutal-pink mx-auto mb-2.5 group-hover:scale-110 transition-transform" />
                      <p className="text-xs font-semibold text-[#1a1a2e]">Tasks</p>
                    </button>
                    <button onClick={() => handleMenuChange('clients')} className="p-5 bg-white/60 backdrop-blur-sm border border-white/40 rounded-2xl hover:bg-white/80 hover:shadow-soft hover:-translate-y-0.5 transition-all duration-200 text-center group" data-testid="quick-action-clients">
                      <Building2 className="w-6 h-6 text-blue-500 mx-auto mb-2.5 group-hover:scale-110 transition-transform" />
                      <p className="text-xs font-semibold text-[#1a1a2e]">Clients</p>
                    </button>
                    <button onClick={() => handleMenuChange('organisation')} className="p-5 bg-white/60 backdrop-blur-sm border border-white/40 rounded-2xl hover:bg-white/80 hover:shadow-soft hover:-translate-y-0.5 transition-all duration-200 text-center group" data-testid="quick-action-employees">
                      <Users className="w-6 h-6 text-purple-500 mx-auto mb-2.5 group-hover:scale-110 transition-transform" />
                      <p className="text-xs font-semibold text-[#1a1a2e]">Employees</p>
                    </button>
                    <button onClick={() => handleMenuChange('accounting')} className="p-5 bg-white/60 backdrop-blur-sm border border-white/40 rounded-2xl hover:bg-white/80 hover:shadow-soft hover:-translate-y-0.5 transition-all duration-200 text-center group" data-testid="quick-action-finance">
                      <Wallet className="w-6 h-6 text-amber-500 mx-auto mb-2.5 group-hover:scale-110 transition-transform" />
                      <p className="text-xs font-semibold text-[#1a1a2e]">Finance</p>
                    </button>
                  </div>
                </div>
              </div>

              <div className="xl:col-span-1">
                <div className="sticky top-20">
                  <SectionErrorBoundary label="Notice Board">
                  <NoticeBoard role="admin" />
                </SectionErrorBoundary>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tasks Page */}
        {activeMenu === 'tasks' && <AdminTasksPage initialFilter={taskFilter} onClearFilter={() => setTaskFilter(null)} />}

        {/* Calendars Page */}
        {activeMenu === 'calendars' && <SectionErrorBoundary label="Calendars"><CalendarsPage api={adminApi} userType="admin" /></SectionErrorBoundary>}

        {/* Human Resources Page */}
        {activeMenu === 'hr' && <HumanResourcesPage />}

        {/* Data Snapshot — Super Admin only export/import for environment mirroring */}
        {activeMenu === 'data-snapshot' && <AdminDataSnapshotPage />}

        {/* Reports — Phase 1 attendance, Phase 2 metrics shown as Pending */}
        {activeMenu === 'reports' && <AdminReportsHub />}

        {/* E-TIMEOFFICE attendance-machine settings (queue + standalone
            attendance pages were consolidated into Reports → Attendance) */}
        {activeMenu === 'etimeoffice' && <EtimeofficeSettingsPage />}

        {/* Clients Page */}
        {activeMenu === 'clients' && <AdminClientsPage />}

        {/* Organisation Page */}
        {activeMenu === 'organisation' && <AdminOrganisationPage />}

        {activeMenu === 'logs' && <AdminAuditLogsPage />}

        {activeMenu === 'settings' && <AdminSettingsPage />}

        {activeMenu === 'helpdesk' && <AdminHelpDeskPage />}

        {/* User Management Page - Super Admin Only */}
        {activeMenu === 'users' && <AdminUserManagement />}
          {activeMenu === 'push' && isSuperAdmin() && <PushNotificationsPage />}

        {/* Accounting Page */}
        {activeMenu === 'accounting' && <AccountingPage />}

        {/* Data Health Page */}
        {activeMenu === 'data-health' && <DataHealthPage />}

        {/* Badge Management Page */}
        {activeMenu === 'badges' && (
          <div className="p-8">
            <AdminBadgeManagement />
          </div>
        )}

        {/* Notices Management Page */}
        {activeMenu === 'notices' && (
          <div className="p-8">
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-[#1a1a2e] tracking-tight">Notice Board</h1>
              <p className="text-sm text-neutral-500 mt-1">Create recognitions and company announcements</p>
            </div>
            <AdminNoticeManagement api={adminApi} />
          </div>
        )}

        {/* Rewards & Recognition Nominations */}
        {activeMenu === 'rewards' && (
          <SectionErrorBoundary label="R&R Nominations">
            <AdminRRConsole api={adminApi} />
          </SectionErrorBoundary>
        )}

        {/* Salary Register — super-admin + HR only */}
        {activeMenu === 'salary-register' && (
          <SectionErrorBoundary label="Salary Register">
            <SalaryRegisterPage api={adminApi} />
          </SectionErrorBoundary>
        )}

        {/* AI Agent Usage Page (admin-only, gated by REACT_APP_ENABLE_AI_AGENT) */}
        {activeMenu === 'agent-usage' && AgentUsagePage && (
          <React.Suspense fallback={<div className="p-8 text-neutral-400">Loading…</div>}>
            <AgentUsagePage />
          </React.Suspense>
        )}

        {/* Other Menu Content Placeholders */}
        {!['dashboard', 'tasks', 'calendars', 'reports', 'hr', 'clients', 'organisation', 'logs', 'settings', 'helpdesk', 'users', 'push', 'accounting', 'data-health', 'data-snapshot', 'data-io', 'notices', 'badges', 'agent-usage', 'etimeoffice', 'etimeoffice-queue', 'attendance', 'rewards', 'salary-register'].includes(activeMenu) && (
          <div className="p-8">
            <h1 className="text-2xl font-bold text-[#1a1a2e] capitalize">{activeMenu}</h1>
            <p className="text-neutral-500 mt-2">Content for {activeMenu} coming soon...</p>
          </div>
        )}
      </main>

      {/* AI Agent (admin-only, gated by REACT_APP_ENABLE_AI_AGENT) — replaces the old ChatWidget */}
      {AgentWidget && (
        <React.Suspense fallback={null}>
          <AgentWidget />
        </React.Suspense>
      )}
    </div>
  );
}
